// =================================================================================================
// >>> **AUTH-BREACH-1 — WHAT A PASSWORD IS, WHAT A CLAIM IS, AND WHAT THE HOUSE SAYS WHILE ASKING
// FOR ONE.** (owner, 2026-09-03, hard stop.) <<<
//
// **THIS FILE IMPORTS NOTHING** — the entry requirement that lets a `.mjs` FAST gate load and RUN
// every clause with no database, no mail rail and no browser (R146). The hashing, the tokens and the
// sending live in `server/account-password.js`; what may be *decided* lives here, once.
//
// >>> **HER FRAMING LAW, AND IT IS NOT A COSMETIC ONE.** <<< Her word: *"'secure your account'
// framing that says the house UPGRADED security, never that it was breached, honest without alarm."*
// That is a narrow line to walk and it is walked in ONE place so no second surface drifts off it:
// every sentence below is true — the house did add password sign-in today, it is an upgrade, and
// nobody is told a lie about what happened. What is refused is the OTHER direction: no sentence here
// may claim her account was accessed, or that we know it was not, because neither is established.
// =================================================================================================

/** How long a claim link lives. Long enough for an email to sit unread through a work day. */
// HER ANSWER (TRUTH-RESET-TTL-Q1 YES, 2026-10-04): a password link expires after ONE HOUR (one shared system, both sites).
export const CLAIM_TTL_MS = 60 * 60 * 1000;

/** The two purposes the one machinery serves (her order 4: forgot-password is born with it). */
export const CLAIM_PURPOSES = Object.freeze(["claim", "reset"]);
export const claimPurposeValid = (p) => CLAIM_PURPOSES.includes(String(p || ""));

/**
 * **THE STRENGTH BAR, ENFORCED GENTLY — HER WORD.**
 *
 * >>> **LENGTH IS THE ONLY HARD RULE, AND THAT IS A DECISION RATHER THAN A SHORTCUT.** <<< Composition
 * rules (a capital, a digit, a symbol) are what produce `Password1!` on every account in the world;
 * length is what actually costs an attacker. Twelve characters, and the only other refusals are the
 * handful of strings that are guessed FIRST — so a member is refused for a real reason or not at all.
 *
 * It returns a REASON rather than a boolean, because a box that says "not strong enough" and nothing
 * else is a box a locked-out member gives up at (R18/R19).
 */
export const PASSWORD_MIN = 12;
export const PASSWORD_MAX = 200;

/** Not a dictionary — the shapes people actually type when told to invent a password right now. */
const OBVIOUS = Object.freeze([
  "password", "passw0rd", "12345678", "123456789", "1234567890", "qwerty", "letmein",
  "iloveyou", "welcome", "admin", "abc123", "monkey", "dragon", "sunshine", "princess",
  "12thhouse", "twelfthhouse", "lala", "astrology",
]);

export function passwordProblem(password, { email = "" } = {}) {
  const value = typeof password === "string" ? password : "";
  if (!value) return "empty";
  if (value.length < PASSWORD_MIN) return "too-short";
  if (value.length > PASSWORD_MAX) return "too-long";
  // Leading/trailing spaces are a support ticket waiting to happen: she cannot see them, and the
  // box she types into next time will trim them for her.
  if (value !== value.trim()) return "edge-space";
  const flat = value.toLowerCase();
  if (OBVIOUS.some((bad) => flat === bad || flat.startsWith(bad))) return "too-obvious";
  // Her own address is the single most-guessed password for her own account.
  const local = String(email || "").toLowerCase().split("@")[0];
  if (local && local.length >= 4 && flat.includes(local)) return "is-your-email";
  return "";
}

/** What she is TOLD, per reason. Plain, warm, and it always says what to do next (R19). */
export const PASSWORD_HINTS = Object.freeze({
  "empty": "Type a password to continue.",
  "too-short": `A little longer — ${PASSWORD_MIN} characters at least. A short phrase you will remember beats a clever word.`,
  "too-long": "That is longer than we can store. Shorten it a little.",
  "edge-space": "That starts or ends with a space, which is easy to lose. Trim it and try again.",
  "too-obvious": "That one is guessed first, every time. Try a short phrase only you would say.",
  "is-your-email": "That has your email inside it, which is the first thing anyone would try. Something else.",
});
export function passwordHint(reason) {
  return Object.prototype.hasOwnProperty.call(PASSWORD_HINTS, reason) ? PASSWORD_HINTS[reason] : "";
}

/**
 * >>> **THE ONE ANSWER THE START ROUTE IS EVER ALLOWED TO GIVE.** <<<
 *
 * Whether or not that address has an account, the house says the SAME sentence. Any difference —
 * a different message, a different status, a measurably different response time — turns this route
 * into a membership oracle: type ten thousand addresses, learn which ones are members. That is a
 * privacy leak in a house whose members do not necessarily want it known they are here.
 *
 * **SO THE COPY IS A CONSTANT, NOT A BRANCH**, and there is no second sentence for it to drift from.
 */
export const CLAIM_SENT_LINE =
  "If that address has an account with us, a link is on its way. It works for one hour. "
  + "Check your spam folder if it is not there in a minute or two.";

/**
 * **THE CLAIM EMAIL'S WORDS — HER FRAMING LAW, AND THE HALF THAT MATTERS IS WHAT IT DOES NOT SAY.**
 *
 * It says: we added password sign-in, set yours, here is the button. TRUE.
 * It never says: your account was breached, or accessed, or that it was not. NEITHER IS ESTABLISHED,
 * and a house that guesses in either direction in a security email is a house nobody believes twice.
 */
export function claimEmailCopy({ name = "", purpose = "claim", letter = "first" } = {}) {
  const who = String(name || "").trim();
  const hello = who ? `${who},` : "Hello,";
  if (purpose === "reset") {
    return {
      subject: "Reset your 12th House password",
      heading: "Set a new password",
      blocks: [
        hello,
        "You asked to reset your password. The button below opens the box where you set a new one. It works for one hour.",
        "If you did not ask for this, you can ignore it — nothing changes until the link is used.",
      ],
      cta: { label: "Set my password" },
    };
  }
  // >>> **THE SECOND LETTER — HER ORDER, 2026-09-04: "SEND the second wave, nobody waits."** <<<
  //
  // **WHY IT EXISTS, MEASURED:** the first wave reached 1,857 members and **1,594 of them still hold
  // no password and no Google seat.** Some never saw it, some meant to and did not, and a fortnight
  // of `signin-refused` rows says 526 real seats were turned away at the door in a single day. Her
  // sentence: *"nobody stays locked out waiting on a first letter that may have missed."*
  //
  // **IT IS ITS OWN BLOCK OF WORDS AND NOT A FLAG SPRINKLED THROUGH THE FIRST**, so the two letters
  // can never drift into each other and a reader of either can tell which one a member got (R28/R29).
  //
  // **WHAT IT DOES NOT SAY IS AGAIN THE HALF THAT MATTERS.** It does not scold her for not acting,
  // it does not say she was locked out, and it does not claim anything happened to her account. It
  // says the door is open and her room is untouched, which is all that is established.
  // **IT IS SELECTED BY `letter`, NOT BY A NEW STORED PURPOSE.** `password_claims.purpose` is a
  // durable column the token path reads, and minting a third value there would ripple into every
  // consumer of a row this letter does not change in any way: it is the same claim, the same token,
  // the same one hour, the same box. Only the WORDS differ, so only the words branch.
  if (letter === "second") {
    return {
      subject: "Your 12th House sign-in is waiting",
      heading: "Your room is still here",
      blocks: [
        hello,
        "We wrote a little while ago to say that signing in at 12th House now uses a password. If that "
          + "letter went astray, or the moment passed, this is the same door held open for you.",
        "The button below opens the box where you set your password. It takes a moment and it works "
          + "for one hour.",
        "Nothing has been touched. Every reading, every chart and everything on your shelf is exactly "
          + "where you left it.",
      ],
      cta: { label: "Open my room" },
    };
  }
  return {
    subject: "Secure your 12th House account",
    heading: "Your account gets a password",
    blocks: [
      hello,
      "We have upgraded how signing in works at 12th House. Until now your email was enough to get you "
        + "back to your room — from today, your account is protected by a password that only you know.",
      "The button below opens the box where you set it. It takes a moment, it works for one hour, and "
        + "from then on you sign in with your email and that password.",
      "Everything in your room is exactly where you left it.",
    ],
    cta: { label: "Secure my account" },
  };
}

/**
 * **THE BANNER FOR A MEMBER WHO IS ALREADY SIGNED IN** — her order 3's second half, so the ones with
 * a live session convert without waiting to be signed out.
 */
export const CLAIM_BANNER_TITLE = "Secure your account";
export const CLAIM_BANNER_BODY =
  "Sign-in just got stronger. Set a password so only you can get into your room.";
export const CLAIM_BANNER_CTA = "Set my password";

/**
 * >>> **WHAT THE DESK SAYS TO A MEMBER WHO WRITES IN LOCKED OUT — HER ORDER 2, AND IT JUMPS THE
 * QUEUE.** <<< Her word: *"anyone writing in about sign-in gets the immediate warm line... rather
 * than waiting in a queue, this class jumps the desk line."* One sentence, no jargon, no apology
 * theatre, and it names where the thing is.
 */
export function lockedOutDeskLine({ name = "", sent = false } = {}) {
  const who = String(name || "").trim();
  const hello = who ? `${who}, ` : "";
  return `${hello}nothing is wrong with your account and nothing has been lost. We upgraded sign-in `
    + `today so your room is protected by a password instead of just your email. `
    + (sent
      ? "We have just sent you the link to set yours — it is in your inbox now, and it works for one hour."
      : "Send yourself the link from the sign-in page and it will land in your inbox within a minute.")
    + " Everything in your room is exactly where you left it.";
}

/**
 * **THE RATE LIMITS.** Two different questions, so two different numbers (R18): how often may ONE
 * address be mailed a link, and how often may one bucket TRY a password. The first protects a
 * member's inbox from being used as a weapon; the second protects her account from being guessed.
 */
export const CLAIM_MINTS_PER_HOUR = 3;
export const SIGNIN_TRIES_PER_15MIN = 10;

/**
 * The sign-in attempt verdict, as a law rather than as an `if` in a route, so the fixture can run
 * every branch. **A WRONG PASSWORD AND AN UNKNOWN ADDRESS ANSWER THE SAME**, for the reason
 * `CLAIM_SENT_LINE` is a constant: the difference is an oracle.
 */
export const SIGNIN_REFUSED_LINE = "That email and password do not match. Try again, or send yourself a reset link.";
export const SIGNIN_LOCKED_LINE = "Too many tries just now. Give it fifteen minutes, or send yourself a reset link.";
export const SIGNIN_NO_PASSWORD_LINE =
  "That account does not have a password yet. Send yourself the link and you can set one in a moment.";
